import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { services, testimonials } from '../data/siteData';

const ServicesPage = ({ onBookingClick }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeService || 'hairCare');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [emblaRefTestimonials, emblaApiTestimonials] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'center',
      slidesToScroll: 1,
      skipSnaps: false
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApiTestimonials) return;
    setSelectedIndex(emblaApiTestimonials.selectedScrollSnap());
  }, [emblaApiTestimonials]);

  useEffect(() => {
    if (!emblaApiTestimonials) return;
    onSelect();
    emblaApiTestimonials.on('select', onSelect);
    emblaApiTestimonials.on('reInit', onSelect);
  }, [emblaApiTestimonials, onSelect]);

  const serviceKeys = Object.keys(services);
  const activeService = services[activeTab];

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  return (
    <>
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Our Services</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Premium Grooming Services
          </h1>
          <p className="text-xl text-gray-300 w-full">
            Comprehensive grooming solutions tailored for Dubai's modern gentleman
          </p>
        </div>
      </section>

      {/* Services Layout - Horizontal Full Width */}
      <section className="py-12 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-12 gap-8">
            
            {/* LEFT SIDE - Service Names */}
            <div className="col-span-12 lg:col-span-3">
              <div className="bg-gray-50 p-6 rounded-lg sticky top-24">
                <h3 className="text-lg font-bold text-black mb-4 uppercase tracking-wide">Select Service</h3>
                <div className="space-y-2">
                  {serviceKeys.map((key) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                        activeTab === key
                          ? 'bg-amber-500 text-black shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-amber-50 hover:text-amber-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{services[key].icon}</span>
                        <span>{services[key].title}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* CENTER - Service Content */}
            <div className="col-span-12 lg:col-span-5">
              <div className="space-y-8">
                {/* Service Header */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-5xl">{activeService.icon}</span>
                    <div>
                      <h2 className="text-3xl font-bold text-black">{activeService.title}</h2>
                      <p className="text-amber-600 font-medium">{activeService.subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Service Overview */}
                <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-amber-500">
                  <h3 className="text-xl font-bold text-black mb-3">Service Overview</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {activeService.overview}
                  </p>
                </div>

                {/* Key Service Details */}
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">Service Details</h3>
                  <div className="space-y-4">
                    {activeService.keyServices.map((service, index) => (
                      <div key={index} className="bg-white p-5 rounded-lg border border-gray-200 hover:border-amber-500 transition-all duration-300">
                        <h4 className="text-lg font-bold text-black mb-2">{service.title}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing Section */}
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">Complete Price List</h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {activeService.services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceClick(service)}
                        className="w-full bg-gray-50 p-4 rounded-lg hover:shadow-md hover:bg-amber-50 transition-all duration-300 border border-gray-200 hover:border-amber-500 cursor-pointer text-left"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-base font-bold text-black mb-1">{service.name}</h4>
                            <p className="text-gray-600 text-sm">{service.description}</p>
                          </div>
                          <span className="text-amber-500 font-bold text-xl ml-4 whitespace-nowrap">{service.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={onBookingClick}
                  className="w-full bg-amber-500 text-black py-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors shadow-lg"
                >
                  {activeService.ctaText}
                </button>
              </div>
            </div>

            {/* RIGHT SIDE - Service Image */}
            <div className="col-span-12 lg:col-span-4">
              <div className="sticky top-24">
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <img 
                    src={activeService.image} 
                    alt={activeService.title}
                    className="w-full h-[600px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{activeService.title}</h3>
                    <p className="text-white/90">{activeService.subtitle}</p>
                  </div>
                </div>

                {/* Service Stats */}
                <div className="mt-6 bg-black text-white p-6 rounded-lg">
                  <h4 className="font-bold text-amber-500 mb-3 uppercase text-sm tracking-wide">Why Choose Us</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-sm">Professional Service</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-sm">Premium Products</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-sm">Experienced Team</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-sm">Affordable Pricing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-black">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Client Reviews</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="overflow-hidden -mx-4" ref={emblaRefTestimonials}>
            <div className="flex items-center">
              {testimonials.map((testimonial, index) => {
                const isCenter = index === selectedIndex % testimonials.length;
                const bgColor = isCenter ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-white';
                const textColor = isCenter ? 'text-white' : 'text-gray-800';
                const serviceColor = isCenter ? 'text-white/90' : 'text-amber-600';
                const starColor = isCenter ? 'fill-white text-white' : 'fill-amber-500 text-amber-500';
                const quoteColor = isCenter ? 'text-white/20' : 'text-gray-200';
                
                return (
                  <div 
                    key={testimonial.id} 
                    className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4 transition-all duration-500"
                    style={{
                      filter: isCenter ? 'blur(0px)' : 'blur(4px)',
                      opacity: isCenter ? 1 : 0.4,
                      transform: isCenter ? 'scale(1.05)' : 'scale(0.95)'
                    }}
                  >
                    <div className={`${bgColor} p-8 shadow-2xl h-full relative overflow-hidden rounded-lg`}>
                      {/* Decorative Element */}
                      <div className={`absolute top-0 right-0 w-32 h-32 ${isCenter ? 'bg-white/10' : 'bg-amber-500/10'} rounded-full -mr-16 -mt-16`}></div>
                      
                      {/* Name and Role */}
                      <div className="mb-4">
                        <h4 className={`font-bold text-xl mb-1 ${textColor}`}>{testimonial.name}</h4>
                        <p className={`text-sm ${textColor} opacity-80`}>{testimonial.role}</p>
                        <p className={`text-xs font-semibold uppercase tracking-wide mt-2 ${serviceColor}`}>
                          {testimonial.service}
                        </p>
                      </div>

                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => {
                          const fillStar = i < Math.floor(testimonial.rating);
                          const halfStar = i === Math.floor(testimonial.rating) && testimonial.rating % 1 !== 0;
                          
                          return (
                            <div key={i} className="relative">
                              <Star size={18} className={fillStar ? starColor : `text-gray-300`} fill={fillStar ? 'currentColor' : 'none'} />
                              {halfStar && (
                                <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
                                  <Star size={18} className={starColor} fill="currentColor" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Quote */}
                      <p className={`${textColor} relative z-10 text-base leading-relaxed`}>
                        "{testimonial.text}"
                      </p>

                      {/* Quote Icon */}
                      <div className={`absolute bottom-4 right-4 ${quoteColor} text-6xl font-serif leading-none`}>"</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Service Detail Modal */}
      {isModalOpen && selectedService && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          {/* Modal Header */}
          <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-amber-600 p-6 flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-1">{selectedService.name}</h3>
              <p className="text-white/90 text-sm">
                {activeService.title} Service
              </p>
            </div>
            <button
              onClick={closeModal}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 space-y-6">
            {/* Price Display */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Service Price</span>
                <span className="text-3xl font-bold text-amber-500">{selectedService.price}</span>
              </div>
            </div>

            {/* Service Description */}
            <div>
              <h4 className="text-lg font-bold text-black mb-3">Service Details</h4>
              <p className="text-gray-600 leading-relaxed">
                {selectedService.description}
              </p>
            </div>

            {/* Service Benefits */}
            <div>
              <h4 className="text-lg font-bold text-black mb-3">What's Included</h4>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">Professional service by experienced staff</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">Premium products and equipment</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">Hygienic and comfortable environment</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">Satisfaction guaranteed</span>
                </div>
              </div>
            </div>

            {/* Service Category Badge */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{activeService.icon}</span>
                <div>
                  <p className="text-sm text-gray-600">Category</p>
                  <p className="font-bold text-black">{activeService.title}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                onClick={() => {
                  closeModal();
                  onBookingClick();
                }}
                className="flex-1 bg-amber-500 text-black py-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors rounded-lg shadow-lg"
              >
                Book This Service
              </button>
              <button
                onClick={closeModal}
                className="px-6 py-4 bg-gray-200 text-gray-700 font-bold uppercase tracking-wider hover:bg-gray-300 transition-colors rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default ServicesPage;
